import React, { useState } from 'react';
import { SpaceBetween, Box, Button } from '@amzn/awsui-components-console';

import './ClockTimePicker.scss';

type Period = 'AM' | 'PM';
type ClockMode = 'hours' | 'minutes';

interface ClockTimePickerProps {
	// 24-hour `HH:MM` value (empty string when no time is selected yet).
	value: string;
	// Emits the selected time back as a 24-hour `HH:MM` string.
	onChange: (value: string) => void;
}

const SIZE = 232;
const CENTER = SIZE / 2;
const FACE_RADIUS = CENTER - 4;
const MARK_RADIUS = 88;

const pad = (num: number) => String(num).padStart(2, '0');

const to24Hour = (hour12: number, minute: number, period: Period): string => {
	const base = hour12 % 12;
	const hours24 = period === 'PM' ? base + 12 : base;

	return `${pad(hours24)}:${pad(minute)}`;
};

const parseTime = (value: string) => {
	if (!value || !value.includes(':')) {
		return { hour12: 12, minute: 0, period: 'AM' as Period };
	}

	const [hours, minutes] = value.split(':').map(Number);
	const period: Period = hours < 12 ? 'AM' : 'PM';
	const hour12 = hours % 12 === 0 ? 12 : hours % 12;

	return { hour12, minute: minutes || 0, period };
};

// Position on the clock face for an angle in degrees (0 = 12 o'clock).
const pointOnFace = (angleDeg: number, radius: number) => {
	const radians = ((angleDeg - 90) * Math.PI) / 180;

	return {
		x: CENTER + radius * Math.cos(radians),
		y: CENTER + radius * Math.sin(radians),
	};
};

export const ClockTimePicker: React.FC<ClockTimePickerProps> = ({
	value,
	onChange,
}) => {
	const { hour12, minute, period } = parseTime(value);
	const [mode, setMode] = useState<ClockMode>('hours');
	const [isDragging, setIsDragging] = useState(false);

	const emit = (nextHour12: number, nextMinute: number, nextPeriod: Period) =>
		onChange(to24Hour(nextHour12, nextMinute, nextPeriod));

	const selectHour = (hour: number) => {
		emit(hour, minute, period);
		setMode('minutes');
	};

	const selectMinute = (min: number) => emit(hour12, min, period);

	const handAngle = mode === 'hours' ? (hour12 % 12) * 30 : minute * 6;
	const hand = pointOnFace(handAngle, MARK_RADIUS);

	const marks =
		mode === 'hours'
			? Array.from({ length: 12 }, (_, index) => index + 1)
			: Array.from({ length: 12 }, (_, index) => index * 5);

	// Maps a pointer position on the face to the nearest hour or minute.
	const applyPointer = (event: React.PointerEvent<SVGSVGElement>) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const dx = event.clientX - rect.left - CENTER;
		const dy = event.clientY - rect.top - CENTER;
		let degrees = (Math.atan2(dy, dx) * 180) / Math.PI + 90;

		if (degrees < 0) {
			degrees += 360;
		}

		if (mode === 'hours') {
			emit(Math.round(degrees / 30) % 12 || 12, minute, period);
		} else {
			emit(hour12, Math.round(degrees / 6) % 60, period);
		}
	};

	const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
		setIsDragging(true);
		applyPointer(event);
	};

	const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
		if (isDragging) {
			applyPointer(event);
		}
	};

	const handlePointerUp = () => {
		if (isDragging && mode === 'hours') {
			setMode('minutes');
		}

		setIsDragging(false);
	};

	return (
		<SpaceBetween size="s">
			<Box textAlign="center">
				<span className="clock-picker__readout">
					<button
						type="button"
						className={
							mode === 'hours'
								? 'clock-picker__unit clock-picker__unit--active'
								: 'clock-picker__unit'
						}
						data-testid="clock-mode-hours"
						onClick={() => setMode('hours')}
					>
						{hour12}
					</button>
					<span className="clock-picker__colon">:</span>
					<button
						type="button"
						className={
							mode === 'minutes'
								? 'clock-picker__unit clock-picker__unit--active'
								: 'clock-picker__unit'
						}
						data-testid="clock-mode-minutes"
						onClick={() => setMode('minutes')}
					>
						{pad(minute)}
					</button>
				</span>
			</Box>

			<div className="clock-picker__face-wrapper">
				<svg
					className="clock-picker__face"
					width={SIZE}
					height={SIZE}
					viewBox={`0 0 ${SIZE} ${SIZE}`}
					role="group"
					aria-label="Clock face"
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onPointerLeave={() => setIsDragging(false)}
				>
					<circle
						cx={CENTER}
						cy={CENTER}
						r={FACE_RADIUS}
						className="clock-picker__dial"
					/>
					<line
						x1={CENTER}
						y1={CENTER}
						x2={hand.x}
						y2={hand.y}
						className="clock-picker__hand"
					/>
					<circle
						cx={hand.x}
						cy={hand.y}
						r={16}
						className="clock-picker__hand-tip"
					/>
					<circle
						cx={CENTER}
						cy={CENTER}
						r={4}
						className="clock-picker__center"
					/>

					{marks.map((mark) => {
						const angle = mode === 'hours' ? (mark % 12) * 30 : mark * 6;
						const position = pointOnFace(angle, MARK_RADIUS);
						const selected =
							mode === 'hours' ? mark === hour12 : mark === minute;

						return (
							<g
								key={mark}
								className="clock-picker__mark"
								data-testid={`clock-${
									mode === 'hours' ? 'hour' : 'minute'
								}-${mark}`}
								onClick={() =>
									mode === 'hours' ? selectHour(mark) : selectMinute(mark)
								}
							>
								<circle
									cx={position.x}
									cy={position.y}
									r={16}
									className="clock-picker__mark-hit"
								/>
								<text
									x={position.x}
									y={position.y}
									textAnchor="middle"
									dominantBaseline="central"
									className={
										selected
											? 'clock-picker__mark-label clock-picker__mark-label--selected'
											: 'clock-picker__mark-label'
									}
								>
									{mode === 'hours' ? String(mark) : pad(mark)}
								</text>
							</g>
						);
					})}
				</svg>
			</div>

			<SpaceBetween direction="horizontal" size="xs">
				<Button
					variant={period === 'AM' ? 'primary' : 'normal'}
					data-testid="clock-period-am"
					onClick={() => emit(hour12, minute, 'AM')}
				>
					AM
				</Button>
				<Button
					variant={period === 'PM' ? 'primary' : 'normal'}
					data-testid="clock-period-pm"
					onClick={() => emit(hour12, minute, 'PM')}
				>
					PM
				</Button>
			</SpaceBetween>
		</SpaceBetween>
	);
};
